import React from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { buildDisplayNameMap } from '../utils/portfolioNames';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const StressTestChart = React.forwardRef(({ stressTestResults, portfolioRegistry }, ref) => {
    const nameMap = buildDisplayNameMap(portfolioRegistry);
    const displayName = (code) => nameMap[code] || code;
    if (!stressTestResults || stressTestResults.length === 0) {
        return (
            <div className="text-center text-muted py-5">
                No stress test data available
            </div>
        );
    }

    // Filter out events with no data (no contributors and zero return)
    const filteredResults = stressTestResults.filter(
        event => event.top_contributors.length > 0 || event.portfolio_return !== 0
    );

    if (filteredResults.length === 0) {
        return (
            <div className="text-center text-muted py-5">
                No stress test data available
            </div>
        );
    }

    // Get all unique asset classes across all events
    const allAssetClasses = new Set();
    filteredResults.forEach(event => {
        event.top_contributors.forEach(contributor => {
            allAssetClasses.add(contributor.asset_class);
        });
    });
    const assetClassArray = Array.from(allAssetClasses);

    // Color palette for asset classes
    const colorPalette = [
        'rgba(0, 51, 102, 0.8)',      // Primary dark blue
        'rgba(240, 165, 0, 0.8)',     // Secondary orange
        'rgba(89, 155, 227, 0.8)',    // Info blue
        'rgba(145, 207, 155, 0.8)',   // Success green
        'rgba(170, 128, 144, 0.8)',   // Dusty pink
        'rgba(57, 174, 197, 0.8)',    // Teal
        'rgba(48, 84, 150, 0.8)',     // Dark blue
        'rgba(255, 193, 7, 0.8)',     // Warning yellow
        'rgba(220, 53, 69, 0.8)',     // Danger red
        'rgba(108, 117, 125, 0.8)',   // Gray
    ];

    // Create datasets for each asset class
    const datasets = assetClassArray.map((assetClass, index) => {
        const data = filteredResults.map(event => {
            const contributor = event.top_contributors.find(
                c => c.asset_class === assetClass
            );
            return contributor ? contributor.contribution * 100 : 0;
        });

        return {
            label: displayName(assetClass),
            data: data,
            backgroundColor: colorPalette[index % colorPalette.length],
            borderColor: colorPalette[index % colorPalette.length].replace('0.8', '1'),
            borderWidth: 1,
        };
    });

    // Add total drawdown as a line/marker dataset
    const totalData = filteredResults.map(event => event.portfolio_return * 100);

    datasets.push({
        label: 'Total Return',
        data: totalData,
        type: 'line',
        borderColor: 'rgba(220, 53, 69, 1)',
        backgroundColor: 'rgba(220, 53, 69, 0.2)',
        borderWidth: 3,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: 'rgba(220, 53, 69, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        fill: false,
        order: 0, // Draw on top
    });

    const chartData = {
        labels: filteredResults.map(event => event.event_name),
        datasets: datasets,
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'nearest',
            intersect: true,
        },
        scales: {
            x: {
                stacked: true,
                grid: {
                    display: false
                },
                ticks: {
                    font: {
                        size: 11
                    }
                }
            },
            y: {
                stacked: true,
                title: {
                    display: true,
                    text: 'Return (%)',
                    font: {
                        size: 12,
                        weight: 'bold'
                    }
                },
                ticks: {
                    callback: function(value) {
                        return value.toFixed(0) + '%';
                    }
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',
                    drawBorder: false
                },
                // Don't cut off at zero - allow negative values to show
                beginAtZero: false
            }
        },
        plugins: {
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    padding: 15,
                    usePointStyle: true,
                    font: {
                        size: 11
                    }
                }
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += context.parsed.y.toFixed(2) + '%';
                        }
                        return label;
                    },
                    title: function(tooltipItems) {
                        // Show event name and total return
                        if (tooltipItems.length > 0) {
                            const eventIndex = tooltipItems[0].dataIndex;
                            const eventName = filteredResults[eventIndex].event_name;
                            const total = filteredResults[eventIndex].portfolio_return * 100;
                            return [eventName, 'Total: ' + total.toFixed(2) + '%'];
                        }
                        return '';
                    }
                }
            },
            title: {
                display: false
            }
        }
    };

    return <Bar ref={ref} data={chartData} options={options} />;
});

export default StressTestChart;
